import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDownIcon, ChevronUpIcon, XMarkIcon, FunnelIcon } from '@heroicons/react/24/outline';
import AutoSuggest from '../FormComponent/AutoSuggest';
import InputBox from '../FormComponent/InputBox';
import Selectbox from '../FormComponent/Selectbox';
import DatePicker from '../FormComponent/DatePicker';
import styles from '../../styles/aICostTracking/CostAnalysisDashboard.module.scss';

const CostAnalysisFilter = ({ onFilterChange, initialFilters = {}, isSidePanel = false, onActiveFiltersChange, filterListData }) => {
    // Helper to normalize filter values (convert strings to arrays for multi-select)
    const normalizeFilterValue = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        return [value];
    };

    const [filters, setFilters] = useState({
        organization: initialFilters.organization || '',
        project: normalizeFilterValue(initialFilters.project),
        user: normalizeFilterValue(initialFilters.user),
        modelversion: normalizeFilterValue(initialFilters.modelversion),
        executionmode: normalizeFilterValue(initialFilters.executionmode),
        startDate: initialFilters.startDate || '',
        endDate: initialFilters.endDate || '',
    });

    const [isExpanded, setIsExpanded] = useState(true);
    const [loading] = useState({
        organizations: false,
        projects: false,
        users: false,
    });

    // Derive options from filterListData prop
    const options = useMemo(() => {
        const defaultOptions = {
            organizations: [],
            projects: [],
            users: [],
            modelversions: [],
            executionmodes: [],
        };

        if (!filterListData) return defaultOptions;

        return {
            organizations: filterListData.organizationList || [],
            projects: filterListData.projectList || [],
            users: filterListData.userList || [],
            modelversions: filterListData.modelList || [],
            executionmodes: filterListData.executionModeList || [],
        };
    }, [filterListData]);

    // Helper to convert filter values (IDs) to option objects for AutoSuggest
    const getOptionObjects = (filterName, filterValues) => {
        if (!Array.isArray(filterValues) || filterValues.length === 0) return [];
        
        // Map filter names to option keys
        const optionKeyMap = {
            organization: 'organizations',
            project: 'projects',
            user: 'users',
            modelversion: 'modelversions',
            executionmode: 'executionmodes',
        };
        
        const optionKey = optionKeyMap[filterName] || filterName;
        const optionList = options[optionKey] || [];
        return filterValues
            .map(val => optionList.find(opt => opt.value === val || opt.id === val))
            .filter(Boolean); // Remove undefined values
    };

    // Helper to extract values from option objects or return as-is
    const extractValues = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) {
            // If array contains objects, extract their values
            return value.map(item => {
                if (typeof item === 'object' && item !== null) {
                    return item.value || item.id || item;
                }
                return item;
            });
        }
        // Single value - could be object or primitive
        if (typeof value === 'object' && value !== null) {
            return [value.value || value.id || value];
        }
        return [value];
    };

    const handleFilterChange = (name, value) => {
        // Normalize value: single value for organization and dates, array for others
        let normalizedValue;
        if (name === 'organization' || name === 'startDate' || name === 'endDate') {
            normalizedValue = (typeof value === 'object' && value !== null && !Array.isArray(value))
                ? (value.value || value.id || value)
                : value;
            
            // Handle clearing (AutoSuggest might pass null)
            if (normalizedValue === null) normalizedValue = '';
        } else {
            normalizedValue = extractValues(value);
        }
        
        const newFilters = { ...filters, [name]: normalizedValue };
        
        // Reset dependent filters when parent filter changes
        if (name === 'organization') {
            newFilters.project = [];
            newFilters.user = [];
        } else if (name === 'project') {
            newFilters.user = [];
        }
        
        setFilters(newFilters);
        if (onFilterChange) {
            onFilterChange(newFilters);
        }
    };

    const handleClearFilters = () => {
        const clearedFilters = {
            organization: '',
            project: [],
            user: [],
            modelversion: [],
            executionmode: [],
            startDate: '',
            endDate: '',
        };
        setFilters(clearedFilters);
        if (onFilterChange) {
            onFilterChange(clearedFilters);
        }
    };

    const hasActiveFilters = useMemo(() => {
        return Object.entries(filters).some(([key, value]) => {
            if (key === 'startDate' || key === 'endDate') {
                return value !== '';
            }
            return Array.isArray(value) ? value.length > 0 : value !== '';
        });
    }, [filters]);

    // Get active filter chips
    const activeFilterChips = useMemo(() => {
        const chips = [];
        const filterLabels = {
            organization: 'Organization',
            project: 'Project',
            user: 'User',
            modelversion: 'Model Version',
            executionmode: 'Execution Mode',
            startDate: 'Start Date',
            endDate: 'End Date',
        };

        Object.entries(filters).forEach(([key, value]) => {
            if (key === 'startDate' || key === 'endDate') {
                if (value) {
                    const date = new Date(value);
                    const formattedDate = date.toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    });
                    chips.push({
                        key,
                        label: filterLabels[key],
                        value: formattedDate,
                    });
                }
            } else if (key === 'organization') {
                if (value) {
                    const option = options.organizations.find(opt => opt.value === value || opt.id === value);
                    chips.push({
                        key,
                        label: filterLabels[key],
                        value: option?.label || value,
                    });
                }
            } else {
                // Handle array values for multi-select
                const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
                if (valueArray.length > 0) {
                    const displayValues = valueArray.map(val => {
                        let displayValue = val;
                        
                        // Get display label for select options
                        if (key === 'organization') {
                            const option = options.organizations.find(opt => opt.value === val);
                            displayValue = option?.label || val;
                        } else if (key === 'project') {
                            const option = options.projects.find(opt => opt.value === val);
                            displayValue = option?.label || val;
                        } else if (key === 'user') {
                            const option = options.users.find(opt => opt.value === val);
                            displayValue = option?.label || val;
                        } else if (key === 'modelversion') {
                            const option = options.modelversions.find(opt => opt.value === val);
                            displayValue = option?.label || val;
                        } else if (key === 'executionmode') {
                            const option = options.executionmodes.find(opt => opt.value === val);
                            displayValue = option?.label || val;
                        }
                        return displayValue;
                    });

                    // Create a chip for each selected value
                    valueArray.forEach((val, index) => {
                        const displayValue = displayValues[index];
                        chips.push({
                            key: `${key}_${index}`,
                            label: filterLabels[key],
                            value: displayValue,
                            originalKey: key,
                            originalValue: val, // Store original value for removal
                        });
                    });
                }
            }
        });
        return chips;
    }, [filters, options]);

    const handleRemoveFilter = (chip) => {
        // Handle removing individual chips from multi-select
        if (chip.originalKey) {
            // This is a multi-select chip - remove the specific value
            const currentValues = Array.isArray(filters[chip.originalKey]) ? filters[chip.originalKey] : [];
            const newValues = currentValues.filter(val => {
                // Compare using the original value stored in the chip
                if (chip.originalValue !== undefined) {
                    return val !== chip.originalValue;
                }
                // Fallback: compare by index if originalValue not available
                return true;
            });
            // If we couldn't match by value, try by index
            if (newValues.length === currentValues.length && chip.key.includes('_')) {
                const index = parseInt(chip.key.split('_')[1], 10);
                if (!isNaN(index) && index >= 0 && index < currentValues.length) {
                    handleFilterChange(chip.originalKey, currentValues.filter((_, i) => i !== index));
                    return;
                }
            }
            handleFilterChange(chip.originalKey, newValues);
        } else {
            // Single value or date filter - clear it
            const emptyValue = (chip.key === 'startDate' || chip.key === 'endDate' || chip.key === 'organization') ? '' : [];
            handleFilterChange(chip.key, emptyValue);
        }
    };

    // Notify parent of active filter chips whenever filters or options change
    useEffect(() => {
        if (onActiveFiltersChange) {
            onActiveFiltersChange(activeFilterChips);
        }
    }, [activeFilterChips, onActiveFiltersChange]);

    // For side panel, always show expanded
    const shouldShowExpanded = isSidePanel ? true : isExpanded;

    return (
        <div className={isSidePanel ? styles.filtersSidePanel : styles.filtersContainer}>
            {/* Header - Only show if not in side panel */}
            {!isSidePanel && (
                <div className={`${styles.filterHeader} ${hasActiveFilters ? styles.hasActiveFilters : ''}`}>
                    <div className={styles.filterHeaderLeft}>
                        <div className={styles.filterIconContainer}>
                            <FunnelIcon style={{ width: 18, height: 18 }} />
                        </div>
                        <h2 className={styles.filterTitle}>
                            Filters
                        </h2>
                        {hasActiveFilters && (
                            <span className={styles.filterChip}>
                                {activeFilterChips.length}
                            </span>
                        )}
                    </div>
                    <div className={styles.filterHeaderRight}>
                        {hasActiveFilters && (
                            <button
                                onClick={handleClearFilters}
                                className={styles.clearButton}
                            >
                                <XMarkIcon style={{ width: 16, height: 16 }} />
                                Clear All
                            </button>
                        )}
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className={styles.expandButton}
                            aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
                        >
                            {isExpanded ? (
                                <ChevronUpIcon style={{ width: 20, height: 20 }} />
                            ) : (
                                <ChevronDownIcon style={{ width: 20, height: 20 }} />
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Clear All Button for Side Panel */}
            {isSidePanel && hasActiveFilters && (
                <div className={styles.filterChipsContainer}>
                    <button
                        onClick={handleClearFilters}
                        className={styles.clearButtonSidePanel}
                    >
                        <XMarkIcon style={{ width: 16, height: 16 }} />
                        Clear All Filters
                    </button>
                </div>
            )}

            {/* Active Filter Chips - Only show in non-side panel mode */}
            {!isSidePanel && hasActiveFilters && activeFilterChips.length > 0 && (
                <div className={styles.filterChipsContainer}>
                    <div className={styles.filterChipsStack}>
                        {activeFilterChips.map((chip) => (
                            <div
                                key={chip.key}
                                className={styles.filterChipItem}
                            >
                                <div className={styles.chipLabelContainer}>
                                    <span className={styles.chipLabel}>
                                        {chip.label}:
                                    </span>
                                    <span className={styles.chipValue}>
                                        {chip.value}
                                    </span>
                                </div>
                                <button
                                    onClick={() => handleRemoveFilter(chip)}
                                    className={styles.chipDeleteButton}
                                    aria-label={`Remove ${chip.label} filter`}
                                >
                                    <XMarkIcon style={{ width: 14, height: 14 }} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Filter Form */}
            <div className={`${styles.filterFormContainer} ${shouldShowExpanded ? styles.expanded : styles.collapsed} ${isSidePanel ? '' : (hasActiveFilters ? styles.hasActiveFilters : styles.noActiveFilters)}`}>
                {isSidePanel ? (
                    // Vertical layout for side panel
                    <div className={styles.filterFormVertical}>
                            <AutoSuggest
                                label="Organization"
                                name="organization"
                                value={options.organizations.find(opt => opt.value === filters.organization || opt.id === filters.organization) || null}
                                options={options.organizations}
                                placeholder="Select organization"
                                loading={loading.organizations}
                                allowClear
                                multiple={false}
                                onChange={(e) => handleFilterChange('organization', e?.target?.value)}
                            />

                            <AutoSuggest
                                label="Project"
                                name="project"
                                value={getOptionObjects('project', filters.project)}
                                options={options.projects}
                                placeholder="Select project"
                                loading={loading.projects}
                                disabled={!filters.organization || filters.organization.length === 0}
                                allowClear
                                multiple={true}
                                onChange={(e) => handleFilterChange('project', e?.target?.value)}
                            />

                            <AutoSuggest
                                label="User"
                                name="user"
                                value={getOptionObjects('user', filters.user)}
                                options={options.users}
                                placeholder="Select user"
                                loading={loading.users}
                                disabled={!filters.organization || filters.organization.length === 0}
                                allowClear
                                multiple={true}
                                onChange={(e) => handleFilterChange('user', e?.target?.value)}
                            />

                            <AutoSuggest
                                label="Model Version"
                                name="modelversion"
                                value={getOptionObjects('modelversion', filters.modelversion)}
                                options={options.modelversions}
                                placeholder="Select model version"
                                allowClear
                                multiple={true}
                                onChange={(e) => handleFilterChange('modelversion', e?.target?.value)}
                            />

                            <Selectbox
                                label="Execution Mode"
                                name="executionmode"
                                value={filters.executionmode}
                                options={options.executionmodes}
                                multiple={true}
                                onChange={(e) => handleFilterChange('executionmode', e?.target?.value)}
                            />

                            <hr className={styles.filterDivider} />

                            <DatePicker
                                label="Start Date"
                                name="startDate"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                className={styles.dateField}
                            />

                            <DatePicker
                                label="End Date"
                                name="endDate"
                                value={filters.endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                className={styles.dateField}
                            />
                    </div>
                ) : (
                    // Grid layout for regular view
                    <div className={styles.filterFormGrid}>
                        {/* Row 1 */}
                        <div className={styles.filterGridItem}>
                                <AutoSuggest
                                    label="Organization"
                                    name="organization"
                                    value={options.organizations.find(opt => opt.value === filters.organization || opt.id === filters.organization) || null}
                                    options={options.organizations}
                                    placeholder="Select organization"
                                    loading={loading.organizations}
                                    allowClear
                                    multiple={false}
                                    onChange={(e) => handleFilterChange('organization', e?.target?.value)}
                                />
                        </div>

                        <div className={styles.filterGridItem}>
                                <AutoSuggest
                                    label="Project"
                                    name="project"
                                    value={getOptionObjects('project', filters.project)}
                                    options={options.projects}
                                    placeholder="Select project"
                                    loading={loading.projects}
                                    disabled={!filters.organization || filters.organization.length === 0}
                                    allowClear
                                    multiple={true}
                                    onChange={(e) => handleFilterChange('project', e?.target?.value)}
                                />
                        </div>

                        {/* Row 2 */}
                        <div className={styles.filterGridItem}>
                                <AutoSuggest
                                    label="User"
                                    name="user"
                                    value={getOptionObjects('user', filters.user)}
                                    options={options.users}
                                    placeholder="Select user"
                                    loading={loading.users}
                                    disabled={!filters.organization || filters.organization.length === 0}
                                    allowClear
                                    multiple={true}
                                    onChange={(e) => handleFilterChange('user', e?.target?.value)}
                                />
                        </div>

                        <div className={styles.filterGridItem}>
                                <AutoSuggest
                                    label="Model Version"
                                    name="modelversion"
                                    value={getOptionObjects('modelversion', filters.modelversion)}
                                    options={options.modelversions}
                                    placeholder="Select model version"
                                    allowClear
                                    multiple={true}
                                    onChange={(e) => handleFilterChange('modelversion', e?.target?.value)}
                                />
                        </div>

                        <div className={styles.filterGridItem}>
                                <Selectbox
                                    label="Execution Mode"
                                    name="executionmode"
                                    value={filters.executionmode}
                                    options={options.executionmodes}
                                    multiple={true}
                                    onChange={(e) => handleFilterChange('executionmode', e?.target?.value)}
                                />
                        </div>

                        {/* Row 3 - Date Range */}
                        <div className={styles.filterGridItem}>
                                <DatePicker
                                    label="Start Date"
                                    name="startDate"
                                    value={filters.startDate}
                                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                    className={styles.dateField}
                                />
                        </div>

                        <div className={styles.filterGridItem}>
                            <DatePicker
                                label="End Date"
                                name="endDate"
                                value={filters.endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                className={styles.dateField}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CostAnalysisFilter;
